import React from "react";
import { Link } from "react-router-dom";

const CONTACT = "strussttechnologies@gmail.com";
const UPDATED = "June 2026";

function LegalShell({ title, tid, children }) {
  return (
    <div className="min-h-screen bg-white text-zinc-800" data-testid={tid}>
      <header className="border-b border-zinc-200 px-6 py-4 flex items-center justify-between">
        <Link to="/" className="flex items-center gap-3">
          <div className="w-9 h-9 bg-zinc-900 text-white grid place-items-center font-heading font-black">M</div>
          <span className="font-heading font-bold tracking-tight">Mind Maker 369</span>
        </Link>
        <nav className="flex gap-5 text-[11px] tracking-[0.14em] uppercase font-semibold text-zinc-500">
          <Link to="/legal/privacy" data-testid="legal-nav-privacy" className="hover:text-zinc-900">Privacy</Link>
          <Link to="/legal/terms" data-testid="legal-nav-terms" className="hover:text-zinc-900">Terms</Link>
          <Link to="/login" data-testid="legal-nav-login" className="hover:text-zinc-900">Sign in</Link>
        </nav>
      </header>
      <main className="max-w-3xl mx-auto px-6 py-12">
        <div className="text-[10px] tracking-[0.2em] uppercase text-zinc-500 font-semibold">Mind Maker 369 · Legal</div>
        <h1 className="font-heading font-black text-4xl sm:text-5xl tracking-tight mt-3 text-zinc-900">{title}</h1>
        <p className="text-xs text-zinc-500 mt-2">Last updated: {UPDATED}</p>
        <div className="prose prose-zinc prose-sm sm:prose-base mt-10 space-y-6 [&_h2]:font-heading [&_h2]:font-bold [&_h2]:text-lg [&_h2]:text-zinc-900 [&_h2]:mt-8 [&_ul]:list-disc [&_ul]:pl-5 [&_ul]:space-y-1">
          {children}
        </div>
      </main>
      <footer className="border-t border-zinc-200 px-6 py-6 text-[11px] tracking-[0.14em] uppercase text-zinc-400 text-center">
        © {new Date().getFullYear()} Mind Maker 369 · Contact: {CONTACT}
      </footer>
    </div>
  );
}

export function PrivacyPolicy() {
  return (
    <LegalShell title="Privacy Policy" tid="privacy-policy-page">
      <p>Mind Maker 369 ("the App") is an operations and client-services platform used by a tax consulting firm and its clients. This policy explains what information we collect, why, and how it is protected.</p>
      <h2>1. Information we collect</h2>
      <ul>
        <li><b>Account data:</b> name, email address, phone number and an encrypted (hashed) password.</li>
        <li><b>Business data:</b> client business name, GSTIN, PAN, invoices, task records and payment status needed to deliver tax and compliance services.</li>
        <li><b>Documents:</b> files you or your consultant upload (e.g. returns, bank statements, KYC). These are stored in encrypted object storage.</li>
        <li><b>Technical data:</b> login timestamps, device/browser type and IP address, used only for security (e.g. brute-force protection).</li>
      </ul>
      <h2>2. How we use it</h2>
      <ul>
        <li>To deliver, track and invoice the professional services you have engaged.</li>
        <li>To send service notifications, deadline reminders, invoices and password-reset emails.</li>
        <li>To secure accounts and prevent unauthorised access.</li>
      </ul>
      <p>We do <b>not</b> sell personal data or use it for third-party advertising.</p>
      <h2>3. Sharing</h2>
      <p>Data is shared only with processors required to run the service: cloud hosting and storage, transactional email (Resend), payment processing (Razorpay) and SMS/WhatsApp notifications (Twilio). Each processor handles data under its own privacy terms and only on our instructions. We may disclose information where required by law or a statutory authority.</p>
      <h2>4. Storage and security</h2>
      <p>Passwords are hashed with bcrypt and never stored in plain text. Sessions use signed, HTTP-only cookies. Access is role-based: employees see only the clients and tasks assigned to them, and clients see only their own portal.</p>
      <h2>5. Retention</h2>
      <p>Records are retained for as long as your engagement is active and thereafter for the period required by Indian tax and accounting law (generally up to 8 years). You may request deletion of data that is not subject to a statutory retention requirement.</p>
      <h2>6. Your rights</h2>
      <p>You may request access to, correction of, or deletion of your personal data by emailing <a href={`mailto:${CONTACT}`}>{CONTACT}</a>. We respond within 30 days.</p>
      <h2>7. Children</h2>
      <p>The App is intended for business use by adults. We do not knowingly collect data from anyone under 18.</p>
      <h2>8. Changes</h2>
      <p>We may update this policy. Material changes will be announced in the App. Continued use after an update constitutes acceptance.</p>
      <h2>9. Contact</h2>
      <p>Data controller: Mind Maker 369 · <a href={`mailto:${CONTACT}`}>{CONTACT}</a></p>
    </LegalShell>
  );
}

export function TermsOfService() {
  return (
    <LegalShell title="Terms of Service" tid="terms-page">
      <p>By creating an account or using Mind Maker 369 ("the App") you agree to these terms. If you are using the App on behalf of a business, you confirm you are authorised to bind that business.</p>
      <h2>1. The service</h2>
      <p>The App provides an operations console for firm staff and a client portal for sharing documents, viewing task progress and paying invoices. The App is a tool that supports professional services; the professional advice itself is governed by your separate engagement with the firm.</p>
      <h2>2. Accounts</h2>
      <ul>
        <li>Each user must have their own login. Do not share credentials.</li>
        <li>You are responsible for activity under your account and must notify us of any suspected unauthorised use.</li>
        <li>Accounts are locked temporarily after repeated failed sign-in attempts.</li>
      </ul>
      <h2>3. Acceptable use</h2>
      <p>You must not upload unlawful content, attempt to access other users' data, reverse-engineer the App, or use it to send spam. We may suspend accounts that breach these terms.</p>
      <h2>4. Documents and data</h2>
      <p>You retain ownership of documents you upload. You grant us a licence to store and process them solely to deliver the services. You are responsible for the accuracy of information you provide.</p>
      <h2>5. Payments</h2>
      <p>Invoices are payable by the due date shown. Online payments are processed by Razorpay; we do not store card or bank details. GST is applied as shown on each invoice. Refunds, if any, are governed by your engagement letter with the firm.</p>
      <h2>6. Availability</h2>
      <p>We aim for high availability but do not guarantee uninterrupted service. Scheduled maintenance may occur outside business hours (Mon–Sat, 09:00–18:00 IST).</p>
      <h2>7. Limitation of liability</h2>
      <p>To the fullest extent permitted by law, the App is provided "as is". We are not liable for indirect or consequential losses arising from use of the App. Nothing in these terms limits liability that cannot be excluded under applicable law.</p>
      <h2>8. Termination</h2>
      <p>You may stop using the App at any time. We may close accounts that are inactive or in breach. Statutory records will be retained as described in our <Link to="/legal/privacy">Privacy Policy</Link>.</p>
      <h2>9. Governing law</h2>
      <p>These terms are governed by the laws of India. Disputes are subject to the exclusive jurisdiction of the courts at the firm's registered place of business.</p>
      <h2>10. Contact</h2>
      <p>Questions about these terms: <a href={`mailto:${CONTACT}`}>{CONTACT}</a></p>
    </LegalShell>
  );
}
